/**
 * The only way the app talks to the backend. Same-origin `/api` (the dev server
 * proxies it). Adds the bearer token, encodes query params, parses JSON, turns
 * failures into ApiError, and optionally validates the body with a Zod schema.
 */
import type { ZodType } from 'zod';
import { contractError, networkError, toApiError } from './errors';

export type QueryValue = string | number | boolean | null | undefined | ReadonlyArray<string | number>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: unknown;
  signal?: AbortSignal;
  /** false = anonymous call (login): no token sent, and a 401 is just an error, not a session expiry. */
  auth?: boolean;
}

interface HttpConfig {
  baseUrl: string;
  getToken: () => string | null;
  onUnauthorized: () => void;
  fetchImpl: typeof fetch;
}

const config: HttpConfig = {
  baseUrl: '/api',
  getToken: () => null,
  onUnauthorized: () => undefined,
  fetchImpl: (...args) => fetch(...args),
};

export function configureHttp(next: Partial<HttpConfig>) {
  Object.assign(config, next);
}

/** Arrays repeat the key (`gameIds=1&gameIds=2`); null/undefined/'' are omitted (the API rejects unknown or empty params). */
export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) for (const v of value as ReadonlyArray<string | number>) params.append(key, String(v));
    else params.append(key, String(value));
  }
  const qs = params.toString();
  return `${config.baseUrl}${path}${qs ? `?${qs}` : ''}`;
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function send(path: string, opts: RequestOptions, accept: string): Promise<Response> {
  const headers: Record<string, string> = { accept };
  const token = opts.auth === false ? null : config.getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  try {
    return await config.fetchImpl(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw networkError(cause);
  }
}

async function failure(res: Response, opts: RequestOptions): Promise<never> {
  const body = await readBody(res);
  if (res.status === 401 && opts.auth !== false) config.onUnauthorized();
  throw toApiError(res.status, body);
}

export async function http<T = unknown>(path: string, opts: RequestOptions = {}, schema?: ZodType<T>): Promise<T> {
  const res = await send(path, opts, 'application/json');
  if (!res.ok) return failure(res, opts);
  const body = await readBody(res);
  if (!schema) return body as T;
  const parsed = schema.safeParse(body);
  if (parsed.success) return parsed.data;
  if (import.meta.env.DEV) console.error(`Contract mismatch for ${opts.method ?? 'GET'} ${path}`, parsed.error.issues);
  throw contractError(parsed.error.issues, body);
}

/** Binary downloads (XLSX/CSV exports). The file name comes from Content-Disposition when present. */
export async function download(
  path: string,
  opts: RequestOptions = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const res = await send(path, opts, '*/*');
  if (!res.ok) return failure(res, opts);
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] ? decodeURIComponent(match[1]) : null };
}
